import React, { useState, useEffect, useRef } from 'react';
import './TimerScreen.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function TimerScreen({ totalSeconds, video, audios, onComplete }) {
  const [timeLeft, setTimeLeft] = useState(totalSeconds);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const [volume, setVolume] = useState(0.9);
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume > 0) {
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      const newMuted = !isMuted;
      setIsMuted(newMuted);
      audioRef.current.muted = newMuted;
    }
  };

  const videoUrl = `${API_URL}/api/static/video/${video}`;
  const audioPlaylist = audios.map(a => `${API_URL}/api/static/audio/${a}`);

  useEffect(() => {
    // Start video and audio playback
    const startPlayback = async () => {
      // Small delay to ensure DOM is ready
      await new Promise(resolve => setTimeout(resolve, 300));
      
      try {
        // Start video first
        if (videoRef.current) {
          videoRef.current.volume = 0;
          videoRef.current.muted = true;
          
          try {
            await videoRef.current.play();
            console.log('Video started');
          } catch (err) {
            console.error('Video play error:', err);
          }
        }

        // Then start audio after a brief delay
        await new Promise(resolve => setTimeout(resolve, 800));
        
        if (audioRef.current && audioPlaylist.length > 0) {
          audioRef.current.volume = 0.9;
          audioRef.current.muted = false;
          
          try {
            await audioRef.current.play();
            console.log('Audio started playing');
          } catch (err) {
            console.error('Audio play error:', err);
            // Continue silently if audio fails
          }
        }
      } catch (err) {
        console.error('Playback error:', err);
      }
      
      // Always start timer
      setIsPlaying(true);
    };

    startPlayback();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, []);

  // Handle audio playlist - play next song when current one ends
  const handleAudioEnded = () => {
    if (audioPlaylist.length === 0 || !audioRef.current) return;
    
    const nextIndex = (currentAudioIndex + 1) % audioPlaylist.length;
    setCurrentAudioIndex(nextIndex);
    
    console.log(`Playing next audio: ${audioPlaylist[nextIndex]}`);
    audioRef.current.src = audioPlaylist[nextIndex];
    audioRef.current.play().catch(err => {
      console.error('Failed to play next audio:', err);
    });
  };

  useEffect(() => {
    if (isPlaying && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            // Stop audio and video
            if (audioRef.current) {
              audioRef.current.pause();
            }
            if (videoRef.current) {
              videoRef.current.pause();
            }
            setTimeout(() => onComplete(), 500);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, timeLeft, onComplete]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="timer-screen" data-testid="timer-screen">
      <video
        ref={videoRef}
        className="background-video"
        src={videoUrl}
        loop
        playsInline
        muted
        autoPlay
        preload="auto"
        data-testid="background-video"
      />
      
      <div className="video-overlay"></div>

      <audio
        ref={audioRef}
        src={audioPlaylist[0]}
        preload="auto"
        onEnded={handleAudioEnded}
        data-testid="background-audio"
      />

      <div className="timer-display bouncing-timer scale-in" data-testid="timer-display">
        <div className="timer-glass">
          <div className="timer-text">{formatTime(timeLeft)}</div>
        </div>
      </div>

      {/* Volume Control */}
      <div 
        className="volume-control"
        onMouseEnter={() => setShowVolumeSlider(true)}
        onMouseLeave={() => setShowVolumeSlider(false)}
      >
        <button 
          className="volume-button"
          onClick={toggleMute}
          data-testid="volume-button"
          title={isMuted ? "Ton einschalten" : "Stumm schalten"}
        >
          {isMuted || volume === 0 ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
            </svg>
          ) : volume < 0.5 ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 9v6h4l5 5V4l-5 5H7z"/>
              <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
          )}
        </button>
        
        {showVolumeSlider && (
          <div className="volume-slider-container">
            <input
              type="range"
              min="0"
              max="100"
              value={isMuted ? 0 : volume * 100}
              onChange={(e) => {
                const newVolume = e.target.value / 100;
                handleVolumeChange(newVolume);
              }}
              className="volume-slider"
              data-testid="volume-slider"
            />
            <span className="volume-percentage">{Math.round(volume * 100)}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default TimerScreen;
